export const PIPELINE_REFERENCE_MODEL_VERSION = 'darcy-colebrook-reference-v1' as const;
export const PIPELINE_REFERENCE_SCORE_VERSION = 'gas-reference-v1' as const;
export const PIPELINE_REFERENCE_FLOW_SOURCE = 'https://www.osti.gov/servlets/purl/2447960';

export interface AssessedPipelineReference {
  status: 'assessed';
  model_version: typeof PIPELINE_REFERENCE_MODEL_VERSION;
  score_version: typeof PIPELINE_REFERENCE_SCORE_VERSION;
  pipeline_id: string;
  pipeline_name: string;
  source: string;
  diameter_inches: number;
  diameter_basis: string;
  distance_miles: number;
  reference_mw: number;
  gas_mmscfd: number;
  gas_dth_day: number;
  scale_points: number;
  proximity_points: number;
  computed_at: string;
}

export interface UnassessedPipelineReference {
  status: 'unassessed';
  model_version: typeof PIPELINE_REFERENCE_MODEL_VERSION;
  score_version: typeof PIPELINE_REFERENCE_SCORE_VERSION;
  reason: string;
  computed_at: string;
}

export type PipelineReference = AssessedPipelineReference | UnassessedPipelineReference;

export interface PipelineReferenceRecord {
  pipeline_reference?: PipelineReference | null;
  dc_score_version?: string | null;
  dc_score_basis?: 'reference_scenario' | 'retained' | 'mixed_or_unknown' | 'unassessed';
}

export interface PipelineReferencePresentation {
  kind: 'reference_scenario' | 'retained' | 'unassessed';
  scoreCopy: 'Reference scenario used in score; gas delivery unverified' | 'Existing score retained; gas reference unassessed' | 'Composite unassessed; gas reference unavailable';
  basisLabel: 'Darcy–Colebrook reference v1' | null;
  gasMmscfd: number | null;
  gasDthDay: number | null;
  netMw: number | null;
}

function nonnegativeNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0;
}

export function isScoredPipelineReference(record: PipelineReferenceRecord): record is PipelineReferenceRecord & { pipeline_reference: AssessedPipelineReference } {
  const reference = record.pipeline_reference;
  return record.dc_score_basis !== 'unassessed'
    && record.dc_score_version === PIPELINE_REFERENCE_SCORE_VERSION
    && reference?.status === 'assessed'
    && reference.model_version === PIPELINE_REFERENCE_MODEL_VERSION
    && reference.score_version === PIPELINE_REFERENCE_SCORE_VERSION
    && nonnegativeNumber(reference.distance_miles) && reference.distance_miles <= 5
    && nonnegativeNumber(reference.reference_mw)
    && nonnegativeNumber(reference.gas_mmscfd)
    && nonnegativeNumber(reference.gas_dth_day)
    && nonnegativeNumber(reference.scale_points)
    && nonnegativeNumber(reference.proximity_points)
    && nonnegativeNumber(reference.diameter_inches) && reference.diameter_inches > 0
    && [reference.pipeline_id, reference.pipeline_name, reference.source, reference.diameter_basis, reference.computed_at]
      .every(value => typeof value === 'string' && value.trim() !== '');
}

export function pipelineReferencePresentation(record: PipelineReferenceRecord): PipelineReferencePresentation {
  if (record.dc_score_basis === 'unassessed') {
    return {
      kind: 'unassessed',
      scoreCopy: 'Composite unassessed; gas reference unavailable',
      basisLabel: null,
      gasMmscfd: null,
      gasDthDay: null,
      netMw: null,
    };
  }
  if (!isScoredPipelineReference(record)) {
    return {
      kind: 'retained',
      scoreCopy: 'Existing score retained; gas reference unassessed',
      basisLabel: null,
      gasMmscfd: null,
      gasDthDay: null,
      netMw: null,
    };
  }
  return {
    kind: 'reference_scenario',
    scoreCopy: 'Reference scenario used in score; gas delivery unverified',
    basisLabel: 'Darcy–Colebrook reference v1',
    gasMmscfd: record.pipeline_reference.gas_mmscfd,
    gasDthDay: record.pipeline_reference.gas_dth_day,
    netMw: record.pipeline_reference.reference_mw,
  };
}

export function formatDcScore(score: number | null | undefined): string {
  return score == null || !Number.isFinite(Number(score)) ? '—' : String(Number(score));
}

// Two significant figures: a reference scenario is not a measurement, so its
// displayed precision must not suggest one.
export function roundReferenceMw(mw: number): number {
  return Number(mw.toPrecision(2));
}

export type PipelinePotential =
  | { kind: 'modeled'; mw: number; displayMw: number; diameterInches: number; distanceMiles: number }
  | { kind: 'recorded'; diameterInches: number }
  | { kind: 'no_line' }
  | { kind: 'unassessed' };

export interface PipelinePotentialRecord extends PipelineReferenceRecord {
  score?: number | null;
  pipeline?: { diameter_in: number | null } | null;
}

// What a portfolio list may say about a site's pipeline. A modeled figure appears
// exactly where the side panel shows one (a scored reference with a composite).
export function pipelinePotential(record: PipelinePotentialRecord): PipelinePotential {
  if (record.score != null && isScoredPipelineReference(record)) {
    const reference = record.pipeline_reference;
    return {
      kind: 'modeled',
      mw: reference.reference_mw,
      displayMw: roundReferenceMw(reference.reference_mw),
      diameterInches: reference.diameter_inches,
      distanceMiles: reference.distance_miles,
    };
  }
  const reference = record.pipeline_reference;
  if (reference == null) {
    const diameter = record.pipeline?.diameter_in;
    return nonnegativeNumber(diameter) && diameter > 0 ? { kind: 'recorded', diameterInches: diameter } : { kind: 'unassessed' };
  }
  if (reference.status === 'unassessed' && reference.reason === 'no_eligible_recorded_gas_transmission') return { kind: 'no_line' };
  return { kind: 'unassessed' };
}

const POTENTIAL_ORDER: Record<PipelinePotential['kind'], number> = { modeled: 0, recorded: 1, no_line: 2, unassessed: 3 };

// Modeled sites by reference MW, nearest pipe first on ties; every other site
// after them, grouped by what is on record and kept in priority order.
export function comparePipelinePotential<T extends PipelinePotentialRecord & { rank: number | null }>(a: T, b: T): number {
  const pa = pipelinePotential(a);
  const pb = pipelinePotential(b);
  if (pa.kind !== pb.kind) return POTENTIAL_ORDER[pa.kind] - POTENTIAL_ORDER[pb.kind];
  if (pa.kind === 'modeled' && pb.kind === 'modeled') {
    if (pa.mw !== pb.mw) return pb.mw - pa.mw;
    if (pa.distanceMiles !== pb.distanceMiles) return pa.distanceMiles - pb.distanceMiles;
  }
  return (a.rank ?? 999) - (b.rank ?? 999);
}
